import React, { useEffect } from "react";
import {
  CrimeReference,
  FILE_URL,
  FurtherInvestigation,
  FurtherInvestigationDecision,
  Roles,
  Suspect,
} from "types";
import { constructFileUrl, DateView, hasRole } from "utils/helper";
import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import FurtherInvestigationFileDecision from "pages/InvestigationFileDetail/fragment/FurtherInvestigationDecision";
import { viewIcon } from "utils/constant";
import { fetchCrimeReferences } from "services/incidentService";
import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type props = {
  file: FurtherInvestigation;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const FurtherInvestigationInformationTab: React.FC<props> = ({
  file,
  setRefetch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const [selectedSuspect, setSelectedSuspect] = React.useState<Suspect>();
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [incident, setIncident] = React.useState<CrimeReference | null>();
  const [showPreviousRequestFiles, setShowPreviousRequestFiles] =
    React.useState(false);

  // Request files (newest first) — a new one is uploaded on each (re)submit.
  const requestDocs = (file?.documents || [])
    .filter((d) => d?.document_type === "additional_investigation_file")
    .slice()
    .sort(
      (a, b) =>
        new Date(b?.created_at || "").getTime() -
        new Date(a?.created_at || "").getTime(),
    );
  const latestRequestFile = requestDocs[0];
  const previousRequestFiles = requestDocs.slice(1);
  const fiBaseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.FURTHER_INVESTIGATION}`;

  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isPolice = loginData?.role === "Member" || loginData?.is_police;

  // Coordinator/registrar sent it back vs approved/forwarded onward.
  const respondBackDecisions = [
    "revice",
    "rejected",
    "back_to_prosecutor",
    "back_coordinator",
    "back_to_registrar",
  ];
  const approvedDecisions = [
    "approve",
    "forward_to_coordinator",
    "forward_to_registrar",
    "forward_to_police",
    "assigned_to_clerk",
  ];
  const isRespondBack = respondBackDecisions.includes(file?.decision || "");
  const isApproved = approvedDecisions.includes(file?.decision || "");
  const hasFeedbacks =
    Array.isArray(file?.feedbacks) && file.feedbacks.length > 0;
  // Show feedbacks (in the sidebar) only once the coordinator has responded.
  // The clerk sees the Incident card here instead of feedbacks.
  const showFeedbacks = (isRespondBack || isApproved) && !isPolice && !isClerk;

  useEffect(() => {
    if (file) {
      fetchCrimeReferences()?.then((data) => {
        if (data) {
          setIncident(
            data?.find(
              (item) =>
                item.Incident_Registration_number ===
                file?.incident_registration_number,
            ) || null,
          );
        }
      });
    }
  }, [file]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* About Investigation File Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-2 h-8 bg-red-600 rounded-full"></div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t("about_investigation_file")}
              </h3>
            </div>

            {(() => {
              // Short key/value fields — only the ones that actually have data.
              const fields = [
                {
                  property: t("prosecutor_registration_number"),
                  value: file?.prosecutor_registration_number,
                },
                {
                  property: t("file_submitted_by"),
                  value: file?.file_submitted_by,
                },
                {
                  property: t("requested_date"),
                  value: file?.date_requested
                    ? DateView(file?.date_requested)
                    : "",
                },
                {
                  property: t("date_approved"),
                  value: file?.date_approved
                    ? DateView(file?.date_approved)
                    : "",
                },
                {
                  property: t("letter_number_sent"),
                  value: file?.letter_number_sent,
                },
                {
                  property: t("letter_number_received"),
                  value: file?.letter_number_received,
                },
                {
                  property: t("date_received_from_police"),
                  value: file?.date_received_from_police
                    ? DateView(file?.date_received_from_police)
                    : "",
                },
                {
                  property: t("number_of_days_given"),
                  value: file?.number_of_days_given,
                },
                {
                  property: t("decision"),
                  value: file?.decision
                    ? t(`further_investigation_decision_${file.decision}`, {
                        defaultValue:
                          FurtherInvestigationDecision[
                            file.decision as keyof typeof FurtherInvestigationDecision
                          ] || file.decision,
                      })
                    : "",
                },
              ].filter(
                (info) =>
                  info.value !== undefined &&
                  info.value !== null &&
                  info.value !== "",
              );

              // Long-text fields — only when present.
              const textFields = [
                {
                  property: t("reason_for_request"),
                  value: file?.reason_for_request,
                },
                {
                  property: t("requesting_body"),
                  value: file?.requesting_body,
                },
                {
                  property: t("decision_reason"),
                  value: file?.decision_reason,
                },
              ].filter((info) => info.value);

              if (fields.length === 0 && textFields.length === 0) {
                return (
                  <p className="text-sm text-gray-500">
                    {t("no_investigation_data_found")}
                  </p>
                );
              }

              return (
                <>
                  {fields.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {fields.map((info, index) => (
                        <div key={index} className="space-y-1">
                          <p className="text-sm font-medium text-gray-600">
                            {info.property}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {info.value as any}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {textFields.length > 0 && (
                    <div className="mt-8 space-y-4">
                      {textFields.map((info, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-lg p-4"
                        >
                          <p className="text-sm font-medium text-gray-600 mb-2">
                            {info.property}
                          </p>
                          <p className="text-sm text-gray-700 whitespace-pre-line">
                            {info.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Suspects (merged into this card) */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-semibold text-gray-900">
                  {t("suspects")}
                </h4>
                {Array.isArray(file?.suspects) && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
                    {file.suspects.length}{" "}
                    {file.suspects.length === 1 ? t("suspect") : t("suspects")}
                  </span>
                )}
              </div>
              {Array.isArray(file?.suspects) && file.suspects.length > 0 ? (
                <div className="space-y-4">
                  {file.suspects.map((suspect, index) => {
                    const imageSrc = (() => {
                      const doc = suspect?.documents?.find(
                        (doc) => doc.document_type === "suspect_picture",
                      );
                      return doc
                        ? constructFileUrl(doc, FILE_URL.SUSPECT)
                        : require("../../../images/imageVector.png");
                    })();
                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <img
                                src={imageSrc}
                                alt={t("suspect")}
                                className="w-16 h-16 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => {
                                  setSelectedSuspect(suspect);
                                  setIsImageModalOpen(true);
                                }}
                              />
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">
                                {`${suspect?.first_name} ${suspect?.middle_name} ${suspect?.last_name}`}
                              </h4>
                              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                                {suspect?.age && (
                                  <span>
                                    {suspect.age} {t("years_old_label")}
                                  </span>
                                )}
                                {suspect?.education_level && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span>{suspect.education_level}</span>
                                  </>
                                )}
                                {suspect?.nationality && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span>{suspect.nationality}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedSuspect(suspect);
                              setIsOpenDetail(true);
                            }}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={t("view_details", { type: t("suspect") })}
                          >
                            {viewIcon}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  {t("no_suspects_available")}
                </div>
              )}
            </div>

            {/* Request file — latest independently, previous in an accordion */}
            {latestRequestFile && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <h4 className="text-base font-semibold text-gray-900 mb-3">
                  {t("further_investigation_request_file", {
                    defaultValue: "Further Investigation Request File",
                  })}
                </h4>
                <div className="space-y-2">
                  <DocumentViewerModal
                    documents={[latestRequestFile] as any}
                    baseUrl={fiBaseUrl}
                    layout="list"
                    hideEmptyFile={true}
                    label={
                      previousRequestFiles.length > 0 ? t("latest_file") : ""
                    }
                  />
                  {previousRequestFiles.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() =>
                          setShowPreviousRequestFiles((v) => !v)
                        }
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      >
                        <span className="text-xs font-medium text-gray-700">
                          {t("previous_files")} ({previousRequestFiles.length})
                        </span>
                        <svg
                          className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
                            showPreviousRequestFiles ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {showPreviousRequestFiles && (
                        <div className="px-3 py-3 border-t border-gray-200">
                          <DocumentViewerModal
                            documents={previousRequestFiles as any}
                            baseUrl={fiBaseUrl}
                            layout="list"
                            hideEmptyFile={true}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Uploaded files — police result & other (request file is in the
              About card above) */}
          {Array.isArray(file?.documents) &&
            file.documents.length > 0 &&
            (() => {
              const resultDocs = file.documents.filter(
                (d) =>
                  d?.document_type === "additional_investigation_result_file",
              );
              const otherDocs = file.documents.filter(
                (d) =>
                  d?.document_type !== "additional_investigation_file" &&
                  d?.document_type !== "additional_investigation_result_file",
              );
              const baseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.FURTHER_INVESTIGATION}`;
              const section = (
                accent: string,
                title: string,
                docs: typeof file.documents,
              ) =>
                docs.length > 0 ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-2 h-8 ${accent} rounded-full`}></div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {title}
                      </h3>
                    </div>
                    <DocumentViewerModal
                      documents={docs as any}
                      baseUrl={baseUrl}
                      layout="list"
                      hide={false}
                      label=""
                      hideEmptyFile={true}
                    />
                  </div>
                ) : null;
              return (
                <>
                  {section(
                    "bg-emerald-600",
                    t("police_result_file", {
                      defaultValue: "Police Result File",
                    }),
                    resultDocs,
                  )}
                  {section(
                    "bg-gray-500",
                    t("uploaded_files", { defaultValue: "Uploaded Files" }),
                    otherDocs,
                  )}
                </>
              );
            })()}

          {/* Action buttons (moved into the main column, no card chrome) */}
          {((file?.decision === "forward_to_coordinator" && isCoordinator) ||
            (file?.decision === "back_coordinator" && isCoordinator) ||
            (file?.decision === "forward_to_registrar" && isRegistrar) ||
            (file?.decision === "back_to_registrar" && isRegistrar) ||
            (file?.decision === "rejected" && isProsecutor) ||
            (file?.decision === "forward_to_police" &&
              !file?.is_efiling &&
              isRegistrar) ||
            (file?.decision === "forward_to_police" &&
              file?.is_efiling &&
              isPolice)) && (
            <FurtherInvestigationFileDecision
              furtherInvestigation={file}
              setRefetch={setRefetch}
              incident={incident}
            />
          )}
        </div>

        {/* Sidebar - Respond-back alert & feedbacks */}
        <div className="space-y-6">
          {/* Coordinator responded back — prosecutor must update/edit */}
          {isRespondBack && !isPolice && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <span className="text-lg leading-none">↩️</span>
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    {t("coordinator_responded_back", {
                      defaultValue: "Responded back",
                    })}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    {t("update_decision_or_edit_hint", {
                      defaultValue:
                        "Review the feedback below, then update the decision or edit the request.",
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Incident card — for the clerk only (replaces feedbacks) */}
          {isClerk && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="bg-red-600 text-white px-4 py-2 inline-block rounded-b-md mb-4">
                {t("incident", { defaultValue: "Incident" })}
              </div>
              <p className="text-gray-600 mb-4">
                {t("manage_incident_details_hint", {
                  defaultValue:
                    "Manage incident details and registration for this investigation file.",
                })}
              </p>
              <button
                className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                onClick={() => {
                  navigate(
                    incident
                      ? "/record-incident/" + incident?.uu_id
                      : "/incident-register",
                    { state: { file, incident } },
                  );
                }}
              >
                {incident
                  ? t("record_incident", { defaultValue: "Record Incident" })
                  : t("register_incident", {
                      defaultValue: "Register Incident",
                    })}
              </button>
            </div>
          )}

          {/* Feedbacks — shown once the coordinator has responded */}
          {showFeedbacks && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-2 h-8 bg-blue-600 rounded-full"></div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("feedbacks_title")}
                </h3>
              </div>

              {hasFeedbacks ? (
                <div className="space-y-3">
                  {file.feedbacks?.map((feedback, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <p className="text-sm text-gray-800 break-words">
                        {feedback?.feedback_text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-sm text-gray-500">
                  {t("no_feedbacks_available")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedSuspect && (
        <SuspectDetailInfo
          suspect={selectedSuspect}
          isOpen={isOpenDetail}
          onClose={() => {
            setIsOpenDetail(false);
            setSelectedSuspect(undefined);
          }}
        />
      )}

      {selectedSuspect && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={(() => {
            const doc = selectedSuspect?.documents?.find(
              (doc) => doc.document_type === "suspect_picture",
            );
            return doc
              ? constructFileUrl(doc, FILE_URL.SUSPECT)
              : require("../../../images/imageVector.png");
          })()}
          label={`${selectedSuspect?.first_name} ${selectedSuspect?.middle_name} ${selectedSuspect?.last_name}`}
          onClose={() => {
            setIsImageModalOpen(false);
            setSelectedSuspect(undefined);
          }}
        />
      )}
    </div>
  );
};

export default FurtherInvestigationInformationTab;
